import { useCanvasCalendarNav } from "@/components/canvas/useCanvasCalendarNav.ts";
import type { CalendarEvent } from "@/types/events.ts";
import { parseISO } from "date-fns";
import { type RefObject, useCallback, useEffect, useRef, useState } from "react";
import { sourceIdOf } from "./eventTargeting.ts";
import { useHighlightedChip } from "./useHighlightedChip.ts";

/**
 * How long to wait for the focused event's chip before giving up on anchoring.
 * Generous because the wait spans a cold app boot: the day jump, the events
 * fetch for that range, and FullCalendar's layout. Nothing bad happens if it
 * fires early — the caller falls back to a surface that needs no chip.
 */
const CHIP_WAIT_MS = 6000;

/** The FullCalendar-shaped events a calendar surface renders. */
type GridEvent = { id?: string; extendedProps: Record<string, unknown> };

/**
 * The id to act on for `event`: the rendered copy's, when the grid is showing
 * one. Two source ents can describe the same meeting (the same event on two
 * connected accounts); the agenda dedupes them to a single chip, so the copy a
 * deep link named isn't necessarily the copy on screen — and the popover reads
 * its event out of the grid cache by exactly this id. `instanceKey` is what
 * survives that dedup, so it's the join. Falls back to the linked event's own
 * id when nothing is rendered.
 */
export function renderedEventId(
  events: GridEvent[],
  event: Pick<CalendarEvent, "gcalId" | "instanceKey">,
): string | undefined {
  const match = event.instanceKey
    ? events.find((e) => e.extendedProps["instanceKey"] === event.instanceKey)
    : undefined;
  return match
    ? sourceIdOf({ id: match.id ?? "", extendedProps: match.extendedProps })
    : event.gcalId;
}

/**
 * Opens an event's click popover from something other than a click — today, a
 * calendar reminder's notification deep link (BIG-6898).
 *
 * The popover anchors to the event's chip, which a deep link doesn't have: the
 * day has to be brought into view and FullCalendar has to paint it first. So
 * this focuses the event — jumping to its day and stamping the `is-highlighted`
 * class that makes the chip findable — waits for the chip, scrolls it into view
 * if the day is scrolled elsewhere, and hands the caller an anchor. The focus
 * ends at the handoff: the popover carries a frozen rect from there, and the
 * ring is a locator, not decoration for an open popover.
 *
 * When no chip can be produced — the event's calendar is hidden, it's collapsed
 * behind a month cell's "+N more", or it simply never resolves — the caller is
 * told to open the event without an anchor instead of leaving the tap dead.
 */
export function useFocusedEventPopover({
  containerRef,
  events,
  onAnchored,
  onUnanchorable,
}: {
  /** The calendar area the chip is painted in. */
  containerRef: RefObject<HTMLElement | null>;
  /** Events currently rendered on the grid, for the id join above. */
  events: GridEvent[];
  /** Open the popover for `eventId`, anchored to its chip. */
  onAnchored: (eventId: string, anchor: HTMLElement) => void;
  /** No chip is coming — open `eventId` on a surface that doesn't need one. */
  onUnanchorable: (eventId: string) => void;
}): {
  /** Focus `event` and open its popover once its chip paints. */
  focusEvent: (event: CalendarEvent) => void;
  /** Feed to the grid so it stamps (and rings) the focused event's chip. */
  highlightedInstanceKey: string | undefined;
} {
  const [focused, setFocused] = useState<CalendarEvent | null>(null);
  const { setCurrentDate } = useCanvasCalendarNav();
  // Latest callbacks without restarting the wait: a caller re-creating them
  // mid-wait would otherwise reset the timer on every render.
  const onAnchoredRef = useRef(onAnchored);
  onAnchoredRef.current = onAnchored;
  const onUnanchorableRef = useRef(onUnanchorable);
  onUnanchorableRef.current = onUnanchorable;
  const eventsRef = useRef(events);
  eventsRef.current = events;

  const focusEvent = useCallback((event: CalendarEvent) => setFocused(event), []);

  // Jump to the focused event's day so there's a chip to anchor to. Keyed on the
  // date string, not the event: a refetch remints the event object and would
  // re-jump the view out from under a user who has since navigated.
  const focusedStart = focused?.start.dateTime;
  useEffect(() => {
    if (focusedStart) setCurrentDate(parseISO(focusedStart));
  }, [focusedStart, setCurrentDate]);

  const { el, isVisible } = useHighlightedChip(focused?.instanceKey, containerRef);

  useEffect(() => {
    if (!focused || !el) return;
    if (!isVisible) {
      // Painted, but scrolled out of the day's window (an event hours from the
      // current scroll position). Bring it in and let the measure loop re-fire
      // with a rect the popover can anchor against.
      el.scrollIntoView({ block: "center", inline: "nearest" });
      return;
    }
    const eventId = renderedEventId(eventsRef.current, focused);
    if (!eventId) return;
    setFocused(null);
    onAnchoredRef.current(eventId, el);
  }, [focused, el, isVisible]);

  useEffect(() => {
    if (!focused) return;
    const timer = setTimeout(() => {
      setFocused(null);
      const eventId = renderedEventId(eventsRef.current, focused);
      if (eventId) onUnanchorableRef.current(eventId);
    }, CHIP_WAIT_MS);
    return () => clearTimeout(timer);
  }, [focused]);

  return { focusEvent, highlightedInstanceKey: focused?.instanceKey };
}
